// oxlint-disable-next-line no-restricted-imports
import { Button as MantineButton } from "@mantine/core";
import React from "react";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { lineHeights } from "#app/theme/v2/lineHeights";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";

import classes from "./Button.module.css";

type ButtonVariant = "primary" | "secondary" | "tertiary" | "destructive" | "link";
type ButtonSize = "sm" | "md" | "lg" | "xl";
type LinkButtonSize = "md" | "xl";

const { button } = colors.bg;

type VariantColors = {
  bg: string;
  hover: string;
  hoverColor?: string;
  color: string;
  border?: string;
  disabledBg: string;
  disabledColor: string;
};

const VARIANT_COLORS: Record<ButtonVariant, VariantColors> = {
  primary: {
    bg: button.primary.default,
    hover: button.primary.hover,
    color: colors.text.primaryInverse,
    disabledBg: button.primary.disabled,
    disabledColor: colors.text.primaryInverse,
  },
  secondary: {
    bg: button.secondary.default,
    hover: button.secondary.hover,
    color: colors.text.primaryDefault,
    disabledBg: button.secondary.disabled,
    disabledColor: colors.text.primaryDisabled,
  },
  tertiary: {
    bg: button.tertiary.default,
    hover: button.tertiary.hover,
    color: colors.text.primaryDefault,
    border: `1px solid ${colors.border.quaternary}`,
    disabledBg: button.tertiary.disabled,
    disabledColor: colors.text.primaryDisabled,
  },
  destructive: {
    bg: button.destructive.default,
    hover: button.destructive.hover,
    color: colors.text.primaryInverse,
    disabledBg: button.destructive.disabled,
    disabledColor: colors.text.errorDisabled,
  },
  link: {
    bg: "transparent",
    hover: "transparent",
    hoverColor: colors.text.primaryHover,
    color: colors.text.primaryDefault,
    border: "none",
    disabledBg: "transparent",
    disabledColor: colors.text.primaryDisabled,
  },
};

const BUTTON_SIZES: Record<
  ButtonSize,
  { height: string; paddingX: string; gap: string; fontSize: string; radius: string }
> = {
  sm: {
    height: spacing[8],
    paddingX: spacing[3],
    gap: spacing[1],
    fontSize: fontSizes[12],
    radius: radius.xl,
  },
  md: {
    height: spacing[9],
    paddingX: spacing[4],
    gap: spacing[2],
    fontSize: fontSizes[14],
    radius: radius.xl,
  },
  lg: {
    height: spacing[10],
    paddingX: spacing[5],
    gap: spacing[3],
    fontSize: fontSizes[14],
    radius: radius.xl,
  },
  xl: {
    height: spacing[12],
    paddingX: spacing[6],
    gap: spacing[3],
    fontSize: fontSizes[16],
    radius: radius.xxl,
  },
};

const LINK_BUTTON_SIZES: Record<
  LinkButtonSize,
  {
    height: string;
    paddingX: string;
    gap: string;
    fontSize: string;
    radius: string;
    lineHeight: string;
    letterSpacing: string;
  }
> = {
  md: {
    height: lineHeights[1.25],
    paddingX: "0px",
    gap: spacing[0.5],
    fontSize: fontSizes[14],
    radius: "0px",
    lineHeight: lineHeights[1.25],
    letterSpacing: "0",
  },
  xl: {
    height: lineHeights[1.5],
    paddingX: "0px",
    gap: spacing[1],
    fontSize: fontSizes[16],
    radius: "0px",
    lineHeight: lineHeights[1.5],
    letterSpacing: "-0.3%",
  },
};

const DARK_TEXT_VARIANTS: ButtonVariant[] = ["secondary", "tertiary", "link"];

function variantStyle(c: VariantColors): React.CSSProperties {
  return {
    "--button-bg": c.bg,
    "--button-hover": c.hover,
    "--button-hover-color": c.hoverColor ?? c.color,
    "--button-color": c.color,
    "--button-bd": c.border ?? "none",
    "--btn-disabled-bg": c.disabledBg,
    "--btn-disabled-color": c.disabledColor,
  } as React.CSSProperties;
}

type SharedButtonProps = {
  children: React.ReactNode;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
  fullWidth?: boolean;
  w?: React.CSSProperties["width"];
};

type ButtonProps = SharedButtonProps &
  (
    | {
        variant?: Exclude<ButtonVariant, "link">;
        size?: ButtonSize;
      }
    | {
        variant: "link";
        size?: LinkButtonSize;
      }
  );

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    children,
    leftSection,
    rightSection,
    disabled,
    loading,
    onClick,
    type = "button",
    fullWidth,
    w,
  } = props;
  const isLink = props.variant === "link";
  const variant = isLink ? "link" : (props.variant ?? "primary");
  const linkMetrics = isLink ? LINK_BUTTON_SIZES[props.size ?? "md"] : undefined;
  const metrics =
    linkMetrics ?? BUTTON_SIZES[props.variant === "link" ? "md" : (props.size ?? "md")];
  const { height, paddingX, gap, fontSize, radius: buttonRadius } = metrics;
  const c = VARIANT_COLORS[variant];
  const loaderColor = DARK_TEXT_VARIANTS.includes(variant)
    ? colors.text.primaryDefault
    : colors.text.primaryInverse;

  return (
    <MantineButton
      ref={ref}
      variant="filled"
      leftSection={leftSection}
      rightSection={rightSection}
      disabled={disabled}
      loading={loading}
      onClick={onClick}
      type={type}
      fullWidth={fullWidth}
      w={w}
      fw="medium"
      loaderProps={{ color: loaderColor }}
      radius={buttonRadius}
      classNames={{
        root: isLink ? [classes.button, classes.buttonLink].join(" ") : classes.button,
        inner: classes.buttonInner,
        section: classes.buttonSection,
      }}
      style={
        {
          ...variantStyle(c),
          "--button-height": height,
          "--button-padding-x": paddingX,
          "--button-fz": fontSize,
          "--btn-gap": gap,
          ...(linkMetrics
            ? { lineHeight: linkMetrics.lineHeight, letterSpacing: linkMetrics.letterSpacing }
            : {}),
        } as React.CSSProperties
      }
    >
      {children}
    </MantineButton>
  );
});
Button.displayName = "Button";

export { Button, BUTTON_SIZES };
export type { ButtonVariant, ButtonSize, LinkButtonSize };
